import { useEffect, useState } from 'react'
import { DataGridPro, type GridColDef, type GridRowsProp } from '@mui/x-data-grid-pro'
import Tooltip from '@mui/material/Tooltip'
import { sortMetricNames } from '@/utils/metrics'

interface ColumnDataItem {
  groupKey: string
  [key: string]: string | number | undefined
}

interface ReportSingleDataGridProps {
  title?: string
  columnData: ColumnDataItem[]
  metricOrder?: string[]
  lastColumn?: GridColDef
  columnKeys: string[]
  listNames?: Record<string, string>
}

export function ReportSingleDataGrid({
  title,
  columnData,
  metricOrder,
  lastColumn,
  columnKeys,
  listNames,
}: ReportSingleDataGridProps) {
  const sortedColumnKeys = columnKeys.sort((a, b) => a.localeCompare(b))

  const columns: GridColDef[] = [
    {
      headerName: 'Metric',
      field: 'metricName',
      width: 200,
      hideable: false,
      disableColumnMenu: true,
    },
    ...sortedColumnKeys.map((key) => {
      const listName = listNames?.[key]
      return {
        headerName: key || 'Unknown',
        field: key,
        width: listName ? 120 : 90,
        disableColumnMenu: true,
        sortable: false,
        filterable: false,
        hideable: false,
        renderHeader: listName
          ? () => {
              const truncatedName = listName.length > 18 ? listName.substring(0, 18) + '...' : listName
              return (
                <Tooltip title={listName} placement="top">
                  <div
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}
                  >
                    <div style={{ fontWeight: 'bold', fontSize: '0.875rem' }}>{key}</div>
                    <div style={{ fontSize: '0.4375rem', color: '#666', marginTop: '2px', cursor: 'default' }}>
                      {truncatedName}
                    </div>
                  </div>
                </Tooltip>
              )
            }
          : undefined,
      }
    }),
    ...(lastColumn
      ? [
          {
            ...lastColumn,
            width: 150,
            hideable: false,
            disableColumnMenu: true,
          },
        ]
      : []),
  ]

  // Transpose columnData to rowsData
  const [rows, setRows] = useState<GridRowsProp>([])

  useEffect(() => {
    const rowsData: Record<string, { id: string; metricName: string; [key: string]: string | number | undefined }> = {}

    columnData.forEach((col) => {
      const groupKey = col.groupKey
      Object.entries(col).forEach(([metricKey, value]) => {
        if (metricKey === 'groupKey') {
          return
        }
        if (!rowsData[metricKey]) {
          rowsData[metricKey] = { id: metricKey, metricName: metricKey }
        }
        rowsData[metricKey][groupKey] = value
      })
    })

    const metricKeys = Object.keys(rowsData)
    const showMetricKeys = metricOrder
      ? metricOrder.filter((key) => metricKeys.includes(key))
      : sortMetricNames(metricKeys)

    setRows(showMetricKeys.map((key) => rowsData[key]))
  }, [columnData, metricOrder])

  return (
    <div className="flex flex-col">
      {title && <h2 className="text-heading font-bold mb-4">{title}</h2>}
      <DataGridPro
        rows={rows}
        columns={columns}
        disableRowSelectionOnClick
        hideFooter
        disableColumnFilter
        disableColumnSelector
        initialState={{
          pinnedColumns: {
            left: ['metricName'],
            right: lastColumn ? [lastColumn.field] : undefined,
          },
          density: 'compact',
        }}
        sx={{
          backgroundColor: 'white',
          '& .MuiDataGrid-columnHeader': {
            fontWeight: 'bold',
            backgroundColor: '#f3f4f6',
          },
          '& .MuiDataGrid-cell--pinnedLeft': {
            fontWeight: 'bold',
            backgroundColor: '#e5e7eb',
          },
          '& .MuiDataGrid-cell--pinnedRight': {
            fontWeight: 'bold',
            backgroundColor: '#e5e7eb',
          },
          '& .MuiDataGrid-columnHeader--pinnedLeft': {
            backgroundColor: '#e5e7eb',
          },
          '& .MuiDataGrid-columnHeader--pinnedRight': {
            backgroundColor: '#e5e7eb',
          },
          '& .MuiDataGrid-virtualScroller': {
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: '8px',
            },
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
        }}
      />
    </div>
  )
}
